/**
 * Scheduled Messages Service
 * API client for scheduled message operations
 */

import { apiClient } from './api.client';

export interface ScheduledMessage {
  id: string;
  user_id: string;
  channel_id: string | null;
  dm_thread_id: string | null;
  content: string;
  scheduled_for: string;
  timezone: string;
  status: 'pending' | 'delivered' | 'failed' | 'cancelled';
  delivered_at: string | null;
  delivered_message_id: string | null;
  failure_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateScheduledMessageRequest {
  content: string;
  scheduledFor: string; // ISO 8601 date string
  timezone: string; // IANA timezone (e.g., 'Europe/Sofia')
  channelId?: string;
  dmThreadId?: string;
}

export interface UpdateScheduledMessageRequest {
  content?: string;
  scheduledFor?: string;
  timezone?: string;
}

class ScheduledMessagesService {
  /**
   * Create a new scheduled message
   */
  async createScheduledMessage(data: CreateScheduledMessageRequest): Promise<ScheduledMessage> {
    const response = await apiClient.post<ScheduledMessage>('/scheduled-messages', data);
    return response.data;
  }

  /**
   * List user's pending scheduled messages
   */
  async listScheduledMessages(): Promise<ScheduledMessage[]> {
    const response = await apiClient.get<ScheduledMessage[]>('/scheduled-messages');
    return response.data;
  }

  /**
   * Update a scheduled message (edit content or reschedule)
   */
  async updateScheduledMessage(
    id: string,
    data: UpdateScheduledMessageRequest
  ): Promise<ScheduledMessage> {
    const response = await apiClient.patch<ScheduledMessage>(`/scheduled-messages/${id}`, data);
    return response.data;
  }

  /**
   * Cancel a scheduled message
   */
  async cancelScheduledMessage(id: string): Promise<void> {
    await apiClient.delete(`/scheduled-messages/${id}`);
  }
}

export const scheduledMessagesService = new ScheduledMessagesService();
